import dayjs from 'dayjs';

/**
 * 生成思考内容模版
 */
export const generateReasoningTemplate = (chunk) => {
  return {
    status: chunk?.status,
    error: null,
    id: chunk?.id,
    type: 'reasoning',
    role: 'assistant',
    content: [
      {
        status: chunk?.status,
        error: null,
        type: 'text',
        index: 0,
        delta: null,
        text: chunk.text || ''
      }
    ]
  };
};

/**
 * 生成工具调用模版
 */
export const generatePluginCallTemplate = (chunk) => {
  return {
    status: chunk?.status,
    error: null,
    id: chunk?.id,
    type: 'plugin_call',
    role: 'assistant',
    content: [
      {
        status: chunk?.status,
        data: {
          call_id: '',
          name: '',
          arguments: ''
        }
      }
    ]
  };
};

/**
 * 生成常规会话模版
 */
export const generateMessageTemplate = (chunk) => {
  return {
    status: chunk?.status,
    error: null,
    id: chunk?.id,
    type: 'message',
    role: 'assistant',
    content: [
      {
        status: chunk?.status,
        type: 'text',
        msg_id: chunk?.id,
        text: chunk?.text || ''
      }
    ]
  };
};

/**
 * 生成系统消息模版
 * @param {object} chunk
 * @param {string} chunk.content - 消息文本
 * @param {string} [chunk.datetime] - 时间戳字符串
 * @param {string} [chunk.role] - 消息角色：system 系统消息，userabort 用户主动暂停
 * @param {string} [chunk.messageType] - 消息类型：error 服务/网络异常，abort 用户主动暂停
 */
export const generateSystemMessageTemplate = (chunk) => {
  return {
    datetime: chunk?.datetime || String(new Date().getTime()),
    role: chunk?.role || 'system',
    status: 'completed',
    error: null,
    type: 'message',
    messageType: chunk?.messageType || 'error',
    content: [
      {
        status: 'completed',
        type: 'text',
        text: chunk?.content || ''
      }
    ]
  };
};

import i18n from '@/plugins/i18n';

/**
 * 历史会话数据转换
 */
export const transformHistoryMessage = (messageData) => {
  const result = [];

  const getMetadataTimestamp = (msg) => {
    if (msg?.metadata?.timestamp) {
      return dayjs(msg.metadata.timestamp).format('YYYY-MM-DD HH:mm');
    }
    return '';
  };

  for (const message of messageData) {
    const metadataTimestamp = getMetadataTimestamp(message);

    // console.info('transformHistoryMessage: message', message);
    if (message.role === 'user') {
      // content 可能是字符串（旧格式）或数组（新格式，含 file/text 条目）
      const contentArr = Array.isArray(message.content) ? message.content : [];

      // 提取文字
      const textItem = contentArr.find((c) => c.type === 'text');
      const textContent =
        textItem?.text ?? (typeof message.content === 'string' ? message.content : message.content?.[0]?.text ?? '');

      // 提取附件（type === 'file' 或 type === 'image'）
      const fileItems = contentArr.filter((c) => c.type === 'file' || c.type === 'image');
      const attachments =
        fileItems.length > 0
          ? fileItems.map((f) => {
              if (f.type === 'image') {
                // 从 image_url 中提取文件名
                const urlParts = (f.image_url || '').split(/[/\\]/);
                const rawName = urlParts[urlParts.length - 1] || 'image';
                const fileName = f.filename || rawName;
                return {
                  uid: f.file_id || `hist_${Math.random().toString(36).substring(2, 8)}`,
                  name: fileName,
                  fileName: fileName,
                  fileUrl: f.image_url,
                  fileType: fileName.split('.').pop()?.toUpperCase() || 'IMAGE',
                  status: 'success'
                };
              }
              return {
                uid: f.file_id || `hist_${Math.random().toString(36).substring(2, 8)}`,
                name: f.filename,
                fileName: f.filename,
                fileUrl: f.file_url,
                fileType: f.filename?.split('.').pop()?.toUpperCase() || '',
                status: 'success'
              };
            })
          : undefined;

      result.push({
        role: 'user',
        id: message.id,
        status: message.status,
        content: textContent,
        datetime: message.datetime ?? metadataTimestamp,
        metadataTimestamp,
        type: 'message',
        attachments
      });
    }

    // 用户手动停止，修改为暂停信息
    if (message?.metadata?.metadata?._is_interrupted) {
      const lastMessage = result[result.length - 1];
      const abortMessage = generateSystemMessageTemplate({
        content: i18n.global.t('openClaw.chatMain.userStoppedGeneration'),
        role: 'userabort',
        messageType: 'abort'
      });

      // 1. 如果最后一个消息是数组，将系统消息，直接添加到数组中
      if (Array.isArray(lastMessage)) {
        result[result.length - 1].push(abortMessage);
      } else {
        // 2. 如果最后一个消息不是数组，将系统消息作为最新消息，push到消息最后一个
        result.push([abortMessage]);
      }

      continue;
    }

    /** assistant 侧多段内容（reasoning / 正文 / 工具）；plugin_call_output 常见 role 为 system，须按 type 建容器。用户也可能是 type: message，须排除 user */
    const isAssistantSideChunk =
      message.role !== 'user' && ['reasoning', 'message', 'plugin_call', 'plugin_call_output'].includes(message.type);
    if (isAssistantSideChunk) {
      const last = result[result.length - 1];
      if (!Array.isArray(last)) {
        result.push([]);
      }
    }

    // 判断是否是思考内容 且上一个元素是数组
    if (message.type === 'reasoning' && Array.isArray(result[result.length - 1])) {
      const reasoningContent = generateReasoningTemplate(message);
      reasoningContent.content = message.content;
      reasoningContent.metadataTimestamp = metadataTimestamp;
      result[result.length - 1].push(reasoningContent);
    }

    // 判断是否是正文回复内容 且上一个元素是数组
    if (message.type === 'message' && Array.isArray(result[result.length - 1])) {
      const chatMessage = generateMessageTemplate(message);
      chatMessage.content = message.content;
      chatMessage.metadataTimestamp = metadataTimestamp;
      result[result.length - 1].push(chatMessage);
    }

    /** 工具调用 */
    if (message.type === 'plugin_call' && Array.isArray(result[result.length - 1])) {
      const pluginCallContent = generatePluginCallTemplate(message);
      pluginCallContent.content = message.content;
      pluginCallContent.metadataTimestamp = metadataTimestamp;
      result[result.length - 1].push(pluginCallContent);
    }

    /** 工具调用输出 找前面的 plugin_call */
    if (message.type === 'plugin_call_output' && Array.isArray(result[result.length - 1])) {
      const output = message.content[0].data.output;
      const call_id = message.content[0].data.call_id;
      const pluginCall = result[result.length - 1].find(
        (item) => item.type === 'plugin_call' && item.content[0].data.call_id === call_id
      );
      if (pluginCall) {
        pluginCall.content[0].data.output = output;
      }
    }
  }

  return result;
};
